import { useLoad } from "@redotech/react-util/load";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-01_filled.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RecoverStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { useContext } from "react";
import { useOptionalAutomationMetricsDateRange } from "../../../../marketing/automations/automation-metrics-date-range-context";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as NodeCSS from "./node.module.css";
import { RecoverAnalyticsBlock } from "./recover-analytics-block";

export interface RecoverNodeData {
  deleteElements: (id: string) => void;
  automationId: string;
  step: PartialWithRequiredType<RecoverStep>;
}

export function RecoverNode(data: {
  id: string;
  data: RecoverNodeData;
  selected?: boolean;
}) {
  const marketingRpcClient = useContext(RedoMarketingRpcClientContext);
  const dateRangeCtx = useOptionalAutomationMetricsDateRange();
  const metricsStart = dateRangeCtx?.dateRange[0].toString();
  const metricsEnd = dateRangeCtx?.dateRange[1].toString();

  const metricsLoad = useLoad(async () => {
    if (!marketingRpcClient) {
      return;
    }
    const metrics = await marketingRpcClient.getRecoverAggregatedMetrics({
      groupBy: {
        automation: data.data.automationId,
        automationStepId: data.id,
      },
      metricsStart,
      metricsEnd,
    });
    return metrics;
  }, [
    marketingRpcClient,
    data.data.automationId,
    data.id,
    metricsStart,
    metricsEnd,
  ]);

  const metrics = metricsLoad.value;

  return (
    <>
      <Handle position={Position.Top} type="target" />
      <BaseNode
        disabled={data.data.step.disabled}
        icon={<RedoFeaturedIcon color="success" Icon={RefreshIcon} size="xs" />}
        onDelete={() => data.data.deleteElements(data.id)}
        selected={data.selected}
        step={data.data.step}
        title="Recover"
      >
        <Flex className={NodeCSS.fullWidth} dir="column" justify="flex-start">
          <Text className={NodeCSS.placeholderText}>
            Queues an initial recovery message to a conversation
          </Text>
        </Flex>
      </BaseNode>
      <RecoverAnalyticsBlock
        initialSends={metrics?.initialSends ?? 0}
        recoveredOrders={metrics?.recoveredOrders ?? 0}
        recoveredRevenue={metrics?.recoveredRevenue ?? 0}
        skippedDueToSkipConditions={metrics?.skippedDueToSkipConditions ?? 0}
        skippedDueToUnsubscribe={metrics?.skippedDueToUnsubscribe ?? 0}
        uniqueCustomerReplies={metrics?.uniqueCustomerReplies ?? 0}
        unsubscribes={metrics?.unsubscribes ?? 0}
      />
      <Handle position={Position.Bottom} type="source" />
    </>
  );
}
